import type { PdfResponse } from "~/types/Pdf";
import { useApiError } from "~/composables/useApiError";
import { isTrimester, getMonthLabel } from "~/utils/monthUtils";

export async function downloadFromUrls(data: PdfResponse) {
  for (let i = 0; i < data.urls.length; i++) {
    const response = await $fetch.raw(data.urls[i].url);
    const blob = new Blob([response._data], {
      type: response.headers?.get("Content-Type") ?? "application/pdf",
    });
    downloadFileFromBlob(blob, data.urls[i].filename);
  }
}

/**
 * Handle bulk download of media files
 * @param items - Array of media items (reports, documents, etc.) to download
 * @param filename - Optional custom filename for the ZIP
 * @param eventBus - Event bus instance for showing toasts
 * @param client - API client instance
 * @param context - Optional context information for filename generation
 */
export async function handleBulkDownload(
  items: any[],
  filename?: string,
  eventBus?: any,
  client?: any,
  context?: {
    cities?: any[];
    selectedNode?: any;
    breadcrumb?: any[];
    itemType?: string;
    period?: string;
  },
) {
  if (!eventBus || !client) {
    throw new Error("EventBus and client are required for bulk download");
  }

  if (items.length === 0) {
    eventBus.emit("toast", {
      severity: "warn",
      summary: "Attention",
      detail: "Aucun élément sélectionné",
    });
    return;
  }

  try {
    const mediaIds = items.map(item => item.id);
    const zipFilename = filename || generateContextualFilename(items, context);

    const response = await client("/api/medias/bulk-download", {
      method: "POST",
      body: {
        media_ids: mediaIds,
        filename: zipFilename,
      },
      responseType: "blob",
    });

    downloadFileFromBlob(response, zipFilename);

    eventBus.emit("toast", {
      severity: "success",
      summary: "Succès",
      detail: `${items.length} élément(s) téléchargé(s) avec succès`,
    });
  }
  catch (err) {
    console.error("Erreur lors du téléchargement groupé:", err);

    const error = useApiError(err);
    let errorData = err.response?._data || err.data || {};

    // If errorData is a Blob, parse it as JSON
    if (errorData instanceof Blob) {
      try {
        const text = await errorData.text();
        errorData = JSON.parse(text);
      }
      catch (parseError) {
        console.error("Failed to parse error blob:", parseError);
        errorData = {};
      }
    }

    // Handle missing files error (no files available)
    if (error.code === 404 && errorData?.data?.missing_files) {
      const missingFiles = errorData.data.missing_files;
      const totalMissing = errorData.data.total_missing || missingFiles.length;
      const totalRequested = errorData.data.total_requested || reports.length;

      eventBus.emit("toast", {
        severity: "error",
        summary: "Aucun fichier disponible",
        detail: `${totalMissing} fichier(s) manquant(s) sur ${totalRequested} sélectionné(s). Téléchargement impossible.`,
        life: 8000,
      });

      // Log details for debugging
      console.warn("Fichiers manquants:", missingFiles);
    }
    else {
      // Use the parsed backend message if available
      const backendMessage = errorData.message || error.message;
      eventBus.emit("toast", {
        severity: "error",
        summary: "Erreur",
        detail:
          backendMessage || "Erreur lors du téléchargement groupé des rapports",
      });
    }
  }
}

/**
 * Generate a contextual filename based on selected data and breadcrumb state
 * @param items - Array of items (reports, documents, etc.) being downloaded
 * @param context - Context information including cities, selected node, breadcrumb, etc.
 * @returns Generated filename string
 */
function generateContextualFilename(
  items: any[],
  context?: {
    cities?: any[];
    selectedNode?: any;
    breadcrumb?: any[];
    itemType?: string; // Generic name instead of reportType
    period?: string;
  },
): string {
  const parts: string[] = [];
  const date = new Date().toISOString().split("T")[0];
  // Add item type filter if specified
  if (context?.itemType) {
    parts.push(sanitizeFilename(context.itemType.replace(/_/g, "-")));
  }
  // Add city information
  if (context?.cities) {
    if (context.cities.length === 1) {
      parts.push(sanitizeFilename(context.cities[0].name));
    }
    else if (context.cities.length > 1) {
      parts.push(`${context.cities.map(c => c.name).join("-")}`);
    }
  }

  // Add breadcrumb context (year, period)
  if (context?.selectedNode) {
    const nodeData = context.selectedNode.data;
    if (nodeData) {
      // Add period (quarter or month)
      if (nodeData.quarter) {
        parts.push(`T${nodeData.quarter}`);
      }
      else if (nodeData.month) {
        // Handle both numeric months (1-12) and trimester strings (T1-T4)
        if (isTrimester(nodeData.month)) {
          parts.push(nodeData.month); // "T1", "T2", etc.
        }
        else {
          const monthNames = [
            "janvier",
            "fevrier",
            "mars",
            "avril",
            "mai",
            "juin",
            "juillet",
            "aout",
            "septembre",
            "octobre",
            "novembre",
            "decembre",
          ];
          const monthNum = typeof nodeData.month === "number"
            ? nodeData.month
            : parseInt(nodeData.month);
          if (monthNum >= 1 && monthNum <= 12) {
            parts.push(monthNames[monthNum - 1]);
          }
        }
      }

      if (nodeData.year) {
        parts.push(nodeData.year.toString());
      }
    }
  }
  else if (context?.period) {
    const periodLabels = {
      monthly: "mensuel",
      quarterly: "trimestriel",
    };
    parts.push(
      periodLabels[context.period as keyof typeof periodLabels]
      || context.period,
    );
  }

  // Handle single item case - use original name
  if (items.length === 1 && items[0].name) {
    const singleItemName = sanitizeFilename(
      items[0].name.replace(/\.[^/.]+$/, ""),
    ); // Remove extension
    if (parts.length > 0) {
      return `${parts.join("_")}_${singleItemName}.zip`;
    }
    else {
      return `${singleItemName}.zip`;
    }
  }

  // Fallback if no context
  if (parts.length === 1) {
    // Multiple items - use generic term
    parts.unshift("documents");
  }
  if (!context?.selectedNode) {
    parts.push(date);
  }

  return `${parts.join("_")}.zip`;
}

/**
 * Sanitize filename by removing invalid characters
 * @param name - Original filename
 * @returns Sanitized filename
 */
function sanitizeFilename(name: string): string {
  return name
    ?.toLowerCase()
    ?.normalize("NFD")
    ?.replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    ?.replace(/[^a-z0-9-]/g, "") // Keep only alphanumeric and hyphens
    ?.replace(/-+/g, "-") // Replace multiple hyphens with single
    ?.replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
}
